import React, { useLayoutEffect } from 'react';
import { StyleSheet, View, } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SettingsSection, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import Amount from 'design-system/Amount';
import CardRow from 'design-system/CardRow';
import ListEmptyView from 'design-system/ListEmptyView';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { formatDateForItem } from 'utils';
import links from 'utils/links';
import useEnsureData from 'hooks/useEnsureData';
import { isWeb } from '../../../constants';
import { selectCreditReportCifasCases, selectCreditReportInsolvencies, selectCreditReportJudgments, selectHasCreditReport, } from '../selectors';
import useGetCreditReport from '../hooks/useGetCreditReport';
import DisputeActionCard from '../components/DisputeActionCard';
const strings = {
    insolvency: {
        headerTitle: 'Bankruptcies and Insolvencies',
        headerSubtitle: 'A bankruptcy order or insolvency will appear on your credit report for a minimum of six years from the date of bankruptcy. Restrictions apply and will limit your ability to get credit',
        emptyTitle: 'No bankruptcies or insolvencies',
        itemPrefix: 'Insolvency',
        sections: {
            caseReference: 'Case reference',
            status: 'Status',
            caseYear: 'Case year',
            courtName: 'Court name',
            orderDate: 'Order date',
            dischargeDate: 'Discharge date',
            orderTypeName: 'Order type',
            name: 'Name',
            address: 'Address',
            // Below are TU fields not surfaced in the current Figma design — strings
            // kept so the commented-out CardRow blocks below remain valid if we
            // re-enable them.
            orderTypeCode: 'Order type code',
            arrangementDate: 'Arrangement date',
            completionDate: 'Completion date',
            revokedDate: 'Revoked date',
            restrictionType: 'Restriction',
            restrictionStartDate: 'Restriction starts',
            restrictionEndDate: 'Restriction ends',
        },
    },
    judgment: {
        headerTitle: 'Court Judgments',
        headerSubtitle: 'A Court Judgment is a type of court order that might be issued if you fail to pay money that you owe. Court Judgment can seriously limit your chances of getting credit',
        emptyTitle: 'No judgments reported',
        itemPrefix: 'Judgment',
        sections: {
            caseNumber: 'Case number',
            status: 'Status',
            courtType: 'Court type',
            courtName: 'Court name',
            amount: 'Amount',
            judgmentDate: 'Judgment date',
            satisfiedDate: 'Satisfied date',
            name: 'Name',
            address: 'Address',
        },
    },
    cfasCases: {
        headerTitle: 'Cifas',
        headerSubtitle: [
            'Cifas is the UK’s fraud prevention service. If you’ve registered for Protective Registration or been flagged as a victim of fraud, it will appear on your report.',
            'This does not affect your credit score. It acts as a warning to lenders that you may be at a higher risk of identity fraud.',
        ],
        learnMore: 'Find out more about Cifas',
        emptyTitle: 'No Cifas warning',
        itemPrefix: 'Case',
        sections: {
            caseId: 'Case ID',
            caseType: 'Case type',
            owningMember: 'Owning member',
            facility: 'Facility',
            supplyDate: 'Supply date',
            expiryDate: 'Expiry date',
            filingReason: 'Filing reason',
            subjectName: 'Subject name',
            subjectRole: 'Subject role',
            // Below are TU fields not surfaced in the current Figma design — strings
            // kept so the commented-out CardRow blocks in CifasCases remain valid if
            // we re-enable them.
            productCode: 'Product code',
            managingMember: 'Managing member',
            applicationDate: 'Application date',
            subjectRoleQualifier: 'Subject role detail',
            subjectAddress: 'Subject address',
            subjectDateOfBirth: 'Subject date of birth',
        },
    },
};
const indexKeyExtractor = (_, index) => String(index);
const SectionSubtitle = ({ text, children, }) => (<View style={[styles.subtitleContainer, styles.subtitleContainerPulledIn]}>
    {Array.isArray(text) ? (<View style={styles.paragraphSpacing}>
        {text.map((t, index) => (<Text TextThin-16 Gray key={index}>
            {t}
          </Text>))}
      </View>) : (<Text TextThin-16 Gray>
        {text}
      </Text>)}
    {children}
  </View>);
const InsolvencyItem = React.memo(({ insolvency, index }) => {
    if (!insolvency)
        return null;
    const { caseReference, orderTypeName, orderDate, dischargeDate, status, caseYear, courtName, name, address,
    // The following fields are available from TU but not surfaced in
    // the current Figma. Keep them destructured so re-enabling the
    // commented-out CardRows below is a one-step change.
    // orderTypeCode,
    // arrangementDate,
    // completionDate,
    // revokedDate,
    // restriction,
     } = insolvency;
    return (<SettingsSection name={`${strings.insolvency.itemPrefix} ${index + 1}`}>
        <Spacer.H4 />
        {!!caseReference && (<CardRow title={strings.insolvency.sections.caseReference} info={caseReference} small tableSpacing/>)}
        {!!status && (<CardRow title={strings.insolvency.sections.status} info={status} small tableSpacing/>)}
        {!!caseYear && (<CardRow title={strings.insolvency.sections.caseYear} info={String(caseYear)} small tableSpacing/>)}
        {!!courtName && (<CardRow title={strings.insolvency.sections.courtName} info={courtName} small tableSpacing/>)}
        {!!orderDate && (<CardRow title={strings.insolvency.sections.orderDate} info={formatDateForItem(orderDate)} small tableSpacing/>)}
        {!!dischargeDate && (<CardRow title={strings.insolvency.sections.dischargeDate} info={formatDateForItem(dischargeDate)} small tableSpacing/>)}
        {!!orderTypeName && (<CardRow title={strings.insolvency.sections.orderTypeName} info={orderTypeName} small tableSpacing/>)}
        {!!name && (<CardRow title={strings.insolvency.sections.name} info={name} small tableSpacing/>)}
        {!!address && (<CardRow title={strings.insolvency.sections.address} info={address} small tableSpacing/>)}
        {/*
          Extra TU fields not in the current Figma. Plumbed end-to-end
          already — uncomment any of these to surface them on this
          screen.
        */}
        {/* {!!orderTypeCode && (
          <CardRow
            title={strings.insolvency.sections.orderTypeCode}
            info={orderTypeCode}
            small
            tableSpacing
          />
        )} */}
        {/* {!!arrangementDate && (
          <CardRow
            title={strings.insolvency.sections.arrangementDate}
            info={formatDateForItem(arrangementDate)}
            small
            tableSpacing
          />
        )} */}
        {/* {!!completionDate && (
          <CardRow
            title={strings.insolvency.sections.completionDate}
            info={formatDateForItem(completionDate)}
            small
            tableSpacing
          />
        )} */}
        {/* {!!revokedDate && (
          <CardRow
            title={strings.insolvency.sections.revokedDate}
            info={formatDateForItem(revokedDate)}
            small
            tableSpacing
          />
        )} */}
        {/* {!!restriction?.type && (
          <CardRow
            title={strings.insolvency.sections.restrictionType}
            info={restriction.type}
            small
            tableSpacing
          />
        )} */}
        {/* {!!restriction?.startDate && (
          <CardRow
            title={strings.insolvency.sections.restrictionStartDate}
            info={formatDateForItem(restriction.startDate)}
            small
            tableSpacing
          />
        )} */}
        {/* {!!restriction?.endDate && (
          <CardRow
            title={strings.insolvency.sections.restrictionEndDate}
            info={formatDateForItem(restriction.endDate)}
            small
            tableSpacing
          />
        )} */}
        <Spacer.H4 />
      </SettingsSection>);
});
const renderInsolvency = ({ item, index }) => (<InsolvencyItem insolvency={item} index={index}/>);
const Insolvencies = ({ scrollHandler, contentContainerStyle, }) => {
    const insolvencies = useAppSelector(selectCreditReportInsolvencies);
    return (<Animated.FlatList data={insolvencies} keyExtractor={indexKeyExtractor} renderItem={renderInsolvency} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.insolvency.headerSubtitle}/>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.insolvency.emptyTitle} style={styles.listEmptyView}/>} ListFooterComponent={<DisputeActionCard params={{ category: 'insolvencies' }}/>}/>);
};
const JudgmentItem = React.memo(({ judgment, index }) => {
    if (!judgment)
        return null;
    const { caseNumber, status, courtType, courtName, amount, currencyCode, judgmentDate, satisfiedDate, name, address, } = judgment;
    return (<SettingsSection name={`${strings.judgment.itemPrefix} ${index + 1}`}>
        <Spacer.H4 />
        {!!caseNumber && (<CardRow title={strings.judgment.sections.caseNumber} info={caseNumber} small tableSpacing/>)}
        {!!status && (<CardRow title={strings.judgment.sections.status} info={status} small tableSpacing/>)}
        {!!courtType && (<CardRow title={strings.judgment.sections.courtType} info={courtType} small tableSpacing/>)}
        {!!courtName && (<CardRow title={strings.judgment.sections.courtName} info={courtName} small tableSpacing/>)}
        {amount !== null && amount !== undefined && (<CardRow title={strings.judgment.sections.amount} info={<Amount Numbers-14 showFraction={false} value={{ value: amount, currency: currencyCode }}/>} small tableSpacing/>)}
        {!!judgmentDate && (<CardRow title={strings.judgment.sections.judgmentDate} info={formatDateForItem(judgmentDate)} small tableSpacing/>)}
        {!!satisfiedDate && (<CardRow title={strings.judgment.sections.satisfiedDate} info={formatDateForItem(satisfiedDate)} small tableSpacing/>)}
        {!!name && (<CardRow title={strings.judgment.sections.name} info={name} small tableSpacing/>)}
        {!!address && (<CardRow title={strings.judgment.sections.address} info={address} small tableSpacing/>)}
        <Spacer.H4 />
      </SettingsSection>);
});
const renderJudgment = ({ item, index }) => (<JudgmentItem judgment={item} index={index}/>);
const Judgments = ({ scrollHandler, contentContainerStyle, }) => {
    const judgments = useAppSelector(selectCreditReportJudgments);
    return (<Animated.FlatList data={judgments} keyExtractor={indexKeyExtractor} renderItem={renderJudgment} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.judgment.headerSubtitle}/>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.judgment.emptyTitle} style={styles.listEmptyView}/>} ListFooterComponent={<DisputeActionCard params={{ category: 'judgments' }}/>}/>);
};
const CifasCaseItem = React.memo(({ cifasCase, index }) => {
    if (!cifasCase)
        return null;
    const { caseId, caseType, owningMember, supplyDate, expiryDate, facility, filingReasons, subjects,
    // The following fields are available from TU but not surfaced in
    // the current Figma. Keep them destructured so re-enabling the
    // commented-out CardRows below is a one-step change.
    // applicationDate,
    // productCode,
    // managingMember,
     } = cifasCase;
    const filingReason = filingReasons?.filter(Boolean).join(', ') || null;
    const primarySubject = subjects?.[0];
    return (<SettingsSection name={`${strings.cfasCases.itemPrefix} ${index + 1}`}>
        <Spacer.H4 />
        {/* Rows ordered to match the Figma "Case 1" card. */}
        {!!caseId && (<CardRow title={strings.cfasCases.sections.caseId} info={caseId} small tableSpacing/>)}
        {!!caseType && (<CardRow title={strings.cfasCases.sections.caseType} info={caseType} small tableSpacing/>)}
        {!!owningMember && (<CardRow title={strings.cfasCases.sections.owningMember} info={owningMember} small tableSpacing/>)}
        {!!facility && (<CardRow title={strings.cfasCases.sections.facility} info={facility} small tableSpacing/>)}
        {!!supplyDate && (<CardRow title={strings.cfasCases.sections.supplyDate} info={formatDateForItem(supplyDate)} small tableSpacing/>)}
        {!!expiryDate && (<CardRow title={strings.cfasCases.sections.expiryDate} info={formatDateForItem(expiryDate)} small tableSpacing/>)}
        {!!filingReason && (<CardRow title={strings.cfasCases.sections.filingReason} info={filingReason} small tableSpacing/>)}
        {!!primarySubject?.name && (<CardRow title={strings.cfasCases.sections.subjectName} info={primarySubject.name} small tableSpacing/>)}
        {!!primarySubject?.subjectRole && (<CardRow title={strings.cfasCases.sections.subjectRole} info={primarySubject.subjectRole} small tableSpacing/>)}
        {/*
          Extra TU fields not in the current Figma. Plumbed end-to-end
          already — uncomment any of these to surface them on this
          screen.
        */}
        {/* {!!productCode && (
          <CardRow
            title={strings.cfasCases.sections.productCode}
            info={productCode}
            small
            tableSpacing
          />
        )} */}
        {/* {!!managingMember && (
          <CardRow
            title={strings.cfasCases.sections.managingMember}
            info={managingMember}
            small
            tableSpacing
          />
        )} */}
        {/* {!!applicationDate && (
          <CardRow
            title={strings.cfasCases.sections.applicationDate}
            info={formatDateForItem(applicationDate)}
            small
            tableSpacing
          />
        )} */}
        {/* {!!primarySubject?.subjectRoleQualifier && (
          <CardRow
            title={strings.cfasCases.sections.subjectRoleQualifier}
            info={primarySubject.subjectRoleQualifier}
            small
            tableSpacing
          />
        )} */}
        {/* {!!primarySubject?.currentAddress && (
          <CardRow
            title={strings.cfasCases.sections.subjectAddress}
            info={primarySubject.currentAddress}
            small
            tableSpacing
          />
        )} */}
        {/* {!!primarySubject?.dateOfBirth && (
          <CardRow
            title={strings.cfasCases.sections.subjectDateOfBirth}
            info={formatDateForItem(primarySubject.dateOfBirth)}
            small
            tableSpacing
          />
        )} */}
        <Spacer.H4 />
      </SettingsSection>);
});
const renderCifasCase = ({ item, index }) => (<CifasCaseItem cifasCase={item} index={index}/>);
const CifasCases = ({ scrollHandler, contentContainerStyle, }) => {
    const cifasCases = useAppSelector(selectCreditReportCifasCases);
    return (<Animated.FlatList data={cifasCases} keyExtractor={indexKeyExtractor} renderItem={renderCifasCase} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.cfasCases.headerSubtitle}>
          <Text Text-16 Primary suppressHighlighting style={styles.learnMore} onPress={() => links.openLink('https://www.cifas.org.uk')}>
            {strings.cfasCases.learnMore}
          </Text>
        </SectionSubtitle>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.cfasCases.emptyTitle} style={styles.listEmptyView}/>}/>);
};
const SECTION_COMPONENTS = {
    insolvency: Insolvencies,
    judgment: Judgments,
    cfasCases: CifasCases,
};
const CreditReportRulingDetailScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route }) => {
    const { type } = route.params;
    const fetchCreditReport = useGetCreditReport();
    useEnsureData(selectHasCreditReport, fetchCreditReport);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const SectionComponent = SECTION_COMPONENTS[type];
    return (<AppView withFrame={false}>
          {SectionComponent ? (<SectionComponent scrollHandler={scrollHandler} contentContainerStyle={contentContainerStyle}/>) : null}
        </AppView>);
}, ({ colors, route }) => ({
    title: strings[route.params.type].headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportRulingDetailScreen;
const styles = StyleSheet.create({
    subtitleContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(8),
        paddingBottom: rem(16),
    },
    subtitleContainerPulledIn: {
        paddingBottom: rem(0),
        marginBottom: rem(-8),
    },
    listEmptyView: {
        marginTop: rem(24),
    },
    paragraphSpacing: {
        gap: rem(8),
    },
    learnMore: {
        marginTop: rem(8),
    },
});
